/**
 * @file Completion toast builders for settled upload sessions.
 * Adapters call {@link showUploadSessionCompleteToast} from `onSessionComplete`.
 */

import { toast } from '@surface/components';

import type { UploadResourceSession } from './types';
import type {
  UploadCompleteToast,
  UploadCompleteToastInput,
  UploadKindAdapter,
} from './upload-kind-adapter';
import type { UploadSessionOutcome } from './upload-session-outcome';

/** Input for the default completion toast builder. */
export type BuildDefaultUploadCompleteToastInput = {
  documentsLabel: string;
  session: UploadResourceSession;
  outcome: UploadSessionOutcome;
};

/**
 * Builds the default success/warning/error completion toast from session outcome.
 * Returns null when all files were cancelled with no successes or errors.
 */
export const buildDefaultUploadCompleteToast = ({
  documentsLabel,
  session,
  outcome,
}: BuildDefaultUploadCompleteToastInput): UploadCompleteToast | null => {
  const { completeCount, errorCount, totalCount, hasPartialSuccess } = outcome;
  const documentsLabelLower = documentsLabel.toLowerCase();

  if (
    completeCount === 0 &&
    errorCount === 0 &&
    outcome.cancelledCount === totalCount
  ) {
    return null;
  }

  if (completeCount === 0 && errorCount > 0) {
    const fileWord = errorCount === 1 ? 'file' : 'files';
    return {
      variant: 'error',
      title: `${documentsLabel} upload failed`,
      description: `${errorCount} ${fileWord} failed for ${session.label}.`,
    };
  }

  if (hasPartialSuccess) {
    const failedWord = errorCount === 1 ? 'file' : 'files';
    return {
      variant: 'warning',
      title: `${completeCount} of ${totalCount} ${documentsLabelLower} uploaded`,
      description: `${errorCount} ${failedWord} failed. Open Uploads to retry.`,
    };
  }

  if (completeCount === 0) {
    return null;
  }

  const completedFiles = session.files.filter(
    (file) => file.status === 'complete'
  );

  if (completeCount === 1 && completedFiles[0]) {
    return {
      variant: 'success',
      title: `${documentsLabel} uploaded`,
      description: `${completedFiles[0].fileName} uploaded to ${session.label}.`,
    };
  }

  const fileWord = completeCount === 1 ? 'file' : 'files';
  return {
    variant: 'success',
    title: `${documentsLabel} uploaded`,
    description: `${completeCount} ${fileWord} uploaded to ${session.label}.`,
  };
};

/**
 * Resolves completion toast from adapter override or default builder.
 * @param adapter - Upload kind adapter (needs `documentsLabel` and optional `getUploadCompleteToast`).
 * @param input - Session and outcome from {@link summarizeUploadSession}.
 */
export const resolveUploadCompleteToast = (
  adapter: Pick<UploadKindAdapter, 'documentsLabel' | 'getUploadCompleteToast'>,
  input: UploadCompleteToastInput
): UploadCompleteToast | null =>
  adapter.getUploadCompleteToast?.(input) ??
  buildDefaultUploadCompleteToast({
    documentsLabel: adapter.documentsLabel,
    session: input.session,
    outcome: input.outcome,
  });

/**
 * Shows the resolved completion toast via `@surface/components` toast API.
 * Call from adapter `onSessionComplete` after query invalidation.
 */
export const showUploadSessionCompleteToast = (
  adapter: Pick<UploadKindAdapter, 'documentsLabel' | 'getUploadCompleteToast'>,
  input: UploadCompleteToastInput
): void => {
  const toastContent = resolveUploadCompleteToast(adapter, input);
  if (!toastContent) {
    return;
  }

  toast[toastContent.variant](toastContent.title, {
    description: toastContent.description,
  });
};
